import React, { useEffect, useState } from 'react';

import { WindowLocation } from '@reach/router';
import {
  GlobalBodyStyles,
  GlobalBodyStylesProps,
  MainContent,
  MainStack,
  MainTopInfoBar,
  StyledMain,
} from './GlobalMain.styled';
import Header, { MainLayout } from 'molecules/Header/Header/Header';
import { TypographyGlobalVariables } from 'styles/typography';
import { ColorPrimitiveGlobalVariables, ShadowGlobalVariables } from 'styles/colors';
import { ColorModeGlobalVariables, GradientGlobalVariables } from 'styles/colors';
import { SpacingGlobalVariables } from 'styles/spacing';
import { ExpandedMenuControllerProvider } from 'src/hooks/useExpandedMenuController';

import 'styles/fonts.scss';
import Footer from 'src/app/organisms/Footer/Footer';

import StandardEmailPopup from 'molecules/EmailPopup/Standard/StandardEmailPopup';
import ProEmailPopup from 'molecules/EmailPopup/Pro/ProEmailPop';
import InfoBar from 'molecules/InfoBar/InfoBar';
import { EmailPopupCustomization } from 'src/hooks/usePopupVisibility';

interface GlobalMainTheme extends GlobalBodyStylesProps {
  hidePopup?: boolean;
}

interface GlobalMainProps<LocationState = WindowLocation['state']> {
  children: React.ReactNode;
  location?: WindowLocation<LocationState>;
  mainLayout?: MainLayout;
  mainTheme?: GlobalMainTheme;
  topbar?: boolean | (() => JSX.Element);
  footer?: boolean | (() => JSX.Element);
  popupCustomization?: EmailPopupCustomization;
}

const GlobalMain = ({
  children,
  location,
  mainLayout = 'standard',
  mainTheme = {},
  topbar = true,
  footer = true,
  popupCustomization,
}: GlobalMainProps): JSX.Element => {
  const [inAppView, setInAppView] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const inAppView = window?.location?.search.includes('hide_chrome=1');
      if (inAppView) {
        window.sessionStorage.setItem('hide_chrome', '1');
        setInAppView(true);
      } else {
        const inAppViewCache = window.sessionStorage.getItem('hide_chrome') === '1';
        setInAppView(inAppViewCache);
      }
    }
  }, []);

  const renderPopup = () => {
    if (inAppView || mainTheme.hidePopup) return null;

    if (mainLayout === 'pro') {
      return <ProEmailPopup customization={popupCustomization} />;
    }

    return <StandardEmailPopup customization={popupCustomization} />;
  };

  const renderFooter = () => {
    if (inAppView || !footer) return null;
    if (typeof footer === 'function') return footer();

    return <Footer type={mainLayout} />;
  };

  const renderTopInfoBar = () => {
    if (inAppView || !topbar) return null;

    // TODO (MKT-9340): TopInfoBar rework
    return <InfoBar />;
  };

  const renderHeader = () => {
    if (inAppView || !topbar) return null;
    if (typeof topbar === 'function') return topbar();

    return (
      <ExpandedMenuControllerProvider>
        <Header
          locationProps={location}
          mainLayout={mainLayout}
        />
      </ExpandedMenuControllerProvider>
    );
  };

  return (
    <StyledMain id='global-main'>
      <SpacingGlobalVariables />
      <ColorPrimitiveGlobalVariables />
      <GradientGlobalVariables />
      <ColorModeGlobalVariables />
      <ShadowGlobalVariables />
      <TypographyGlobalVariables />
      <GlobalBodyStyles backgroundColor={mainTheme.backgroundColor} />
      <MainStack direction='column'>
        <MainTopInfoBar>{renderTopInfoBar()}</MainTopInfoBar>
        <MainContent id='main-content'>
          {renderPopup()}
          {renderHeader()}
          {children}
          {renderFooter()}
        </MainContent>
      </MainStack>
    </StyledMain>
  );
};

export default GlobalMain;
